import { areDirectoryIdsEqual, isRootDirectory } from 'shared/domain/directory/directoryIds';
import type { DirectoryUrlSyncInput } from './types';

/**
 * Directory the URL should switch to after the store changed directory, or
 * `undefined` when the URL must stay as it is. `ROOT_DIR_ID` means the
 * documentation root.
 *
 * Folder navigation (double click, breadcrumbs, parent column) only updates the
 * store, so this keeps the URL in sync at any folder depth - otherwise a page
 * refresh reloads a stale (parent) directory from the URL.
 */
export function resolveStoreDrivenDirectoryUrl({
  previousStoreDirectory,
  storeDirectory,
  urlDirectoryId,
}: DirectoryUrlSyncInput): string | undefined {
  // Store did not change: a URL-driven change (back/link) or a re-render.
  // Writing the URL here would bounce it back to the previous directory.
  if (areDirectoryIdsEqual(previousStoreDirectory, storeDirectory)) {
    return undefined;
  }

  if (areDirectoryIdsEqual(storeDirectory, urlDirectoryId)) {
    return undefined;
  }

  // Store briefly empty while the URL still points to another directory (load,
  // backtrack) - wait for URL -> store sync instead of redirecting to root.
  // An intentional root navigation starts from the directory shown in the URL.
  const isTransientRoot =
    isRootDirectory(storeDirectory) &&
    !isRootDirectory(urlDirectoryId) &&
    !areDirectoryIdsEqual(previousStoreDirectory, urlDirectoryId);
  if (isTransientRoot) {
    return undefined;
  }

  return storeDirectory;
}
